import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createAuthLinkGrantsClient, type AuthLinkGrantsClient } from './auth-link-grants-client'
import { createMindsetClient, type MindsetClient } from './mindset-client'
import { createShenAiClient, type ShenAiClient } from './shenai-client'
import { createScanClient, type ScanClient } from './scan-client'
import type { RuntimeConfig } from '@/lib/runtime-config'

interface ApiClients {
  readonly auth: AuthLinkGrantsClient
  readonly scan: ScanClient
  /** The vendor-specific route, on the same origin as the scan routes (docs/decisions.md D-11). */
  readonly mindset: MindsetClient
  readonly shenai: ShenAiClient
}

const ApiClientsContext = createContext<ApiClients | null>(null)

export function ApiClientsProvider({
  config,
  children,
}: {
  config: RuntimeConfig
  children: ReactNode
}) {
  const value = useMemo<ApiClients>(
    () => ({
      auth: createAuthLinkGrantsClient(config.authApiBaseUrl),
      scan: createScanClient(config.scanApiBaseUrl),
      mindset: createMindsetClient(config.scanApiBaseUrl),
      shenai: createShenAiClient(config.scanApiBaseUrl),
    }),
    [config.authApiBaseUrl, config.scanApiBaseUrl],
  )
  return <ApiClientsContext.Provider value={value}>{children}</ApiClientsContext.Provider>
}

export function useApiClients(): ApiClients {
  const value = useContext(ApiClientsContext)
  if (!value) {
    throw new Error('useApiClients() called outside an ApiClientsProvider')
  }
  return value
}
